import { useTranslation } from 'react-i18next'
import { PricingModelFragment, ProjectFragment } from '../generated/graphql'
import { usePricingModels } from './use-pricing-models'
import { useAlert } from '../components/alert/AlertProvider'
import { useNotifications } from '../notifications/use-notifications'

type UseOnDeletePricingModel = {
  /**
   * Handle starting the princing model delete process by opening an alert dialog.
   *
   * @param pricingModel The pricing model to be removed.
   * @param [project] Provide the project fragment when removing from a project. Omit when removing global pricing model.
   */
  onDeletePricingModel(
    pricingModel: PricingModelFragment,
    project?: ProjectFragment,
  ): void
}

export function useOnDeletePricingModel(): UseOnDeletePricingModel {
  const { deletePricingModel } = usePricingModels()
  const { alert } = useAlert()
  const { enqueueError } = useNotifications()
  const { t } = useTranslation()

  const onDeletePricingModel = (
    pricingModel: PricingModelFragment,
    project?: ProjectFragment,
  ) => {
    if (
      project?.versions.some(
        (version) => version.pricingModel?.id === pricingModel.id,
      )
    ) {
      enqueueError(t('pricingModels.unableToDelete'))
      return
    }

    alert(
      t('pricingModels.deleteDialog.action'),
      t('pricingModels.deleteDialog.message', { name: pricingModel.name }),
      [
        { title: t('common.cancel') },
        {
          title: t('pricingModels.deleteDialog.action'),
          color: 'secondary',
          onPress: async () => {
            await deletePricingModel(pricingModel.id, project)
          },
          dataCy: 'confirm-delete-pricing-model',
        },
      ],
    )
  }

  return {
    onDeletePricingModel,
  }
}
